import logger from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, ArrowRight, Loader2, X } from 'lucide-react';
import { fetchEvents } from '../services/api';

const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0);
  }
  return new Date(dateStr + 'T12:00:00');
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchEvents();
        setEvents(data);
      } catch (err) {
        logger.error('Failed to fetch events:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="hero-section" style={{ minHeight: '50vh' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2f23] to-[#2a5a3d]" />
        <div className="absolute inset-0 z-2 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '400px',
          backgroundRepeat: 'repeat'
        }} />
        <div className="hero-content container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto animate-fade-in">
            <div className="islamic-divider mb-8"><div className="islamic-star" /></div>
            <h1 className="font-display text-5xl md:text-6xl text-white mb-6" data-testid="events-page-title">Events & Programs</h1>
            <p className="text-white/70 text-lg">Upcoming gatherings, majalis, and community programs.</p>
            <div className="islamic-divider mt-8"><div className="islamic-star" /></div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="section">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-8 h-8 text-[#c5a572] animate-spin" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-12 h-12 text-[#c5a572]/40 mx-auto mb-4" />
              <p className="text-[#5a6d62] text-lg">No upcoming events at this time.</p>
              <p className="text-[#5a6d62] text-sm mt-2">Check back soon for new programs and gatherings.</p>
            </div>
          ) : (
            <div className="grid-3">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="card card-gold group overflow-hidden cursor-pointer"
                  onClick={() => setSelectedEvent(event)}
                  data-testid={`event-card-${event.id}`}
                >
                  {event.poster_url && (
                    <div className="relative -mx-8 -mt-8 mb-6 h-48 overflow-hidden">
                      <img src={event.poster_url} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <span className="text-[#c5a572] text-xs uppercase tracking-[0.25em] bg-black/30 px-2 py-1 backdrop-blur-sm">
                          {parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'long' })}
                        </span>
                        <div className="text-4xl text-white mt-1 tracking-wider font-bold drop-shadow-lg">
                          {parseLocalDate(event.date).getDate()}
                        </div>
                      </div>
                    </div>
                  )}
                  {!event.poster_url && (
                    <div className="mb-6">
                      <span className="text-[#c5a572] text-xs uppercase tracking-[0.25em]">
                        {parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'long' })}
                      </span>
                      <div className="text-5xl text-[#1a2f23] mt-1 tracking-wider">
                        {parseLocalDate(event.date).getDate()}
                      </div>
                    </div>
                  )}
                  <h3 className="text-xl text-[#1a2f23] mb-4 group-hover:text-[#c5a572] transition-colors tracking-wide font-medium">{event.title}</h3>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-3 text-[#5a6d62]">
                      <Clock className="w-4 h-4 text-[#c5a572]" />
                      <span className="text-sm">{event.time}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[#5a6d62]">
                      <MapPin className="w-4 h-4 text-[#c5a572]" />
                      <span className="text-sm">{event.location}</span>
                    </div>
                  </div>
                  <div className="text-sm text-[#5a6d62] leading-relaxed line-clamp-3 rich-text-content" dangerouslySetInnerHTML={{ __html: event.description }} />
                  <p className="text-xs text-[#c5a572] mt-4 opacity-0 group-hover:opacity-100 transition-opacity">Click for details →</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Event Modal */}
      {selectedEvent && <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
    </div>
  );
};

const EventModal = ({ event, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[9999]" onClick={onClose} data-testid="event-modal">
      <div className="fixed inset-0 bg-black/90" />
      <div className="absolute top-[180px] left-0 right-0 bottom-0 flex items-start justify-center px-8 overflow-y-auto">
        <div className="relative bg-white w-full max-w-3xl shadow-2xl rounded-lg flex flex-col lg:flex-row" onClick={(e) => e.stopPropagation()}>
          <div className="flex-1 p-6 lg:p-8 order-2 lg:order-1">
            <button onClick={onClose} className="lg:hidden absolute top-3 right-3 w-8 h-8 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex items-center justify-center" data-testid="event-modal-close">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-14 h-14 bg-[#1a2f23] text-white flex flex-col items-center justify-center rounded">
                <span className="text-[10px] uppercase tracking-wider text-[#c5a572]">{parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                <span className="text-xl font-bold">{parseLocalDate(event.date).getDate()}</span>
              </div>
              <div>
                <h2 className="text-xl lg:text-2xl font-display text-[#1a2f23] tracking-wide">{event.title}</h2>
                {event.event_type && event.event_type !== 'general' && (
                  <span className="text-[10px] bg-[#c5a572]/20 text-[#8a7045] px-2 py-0.5 rounded uppercase tracking-wider mt-1 inline-block">{event.event_type}</span>
                )}
              </div>
            </div>
            <div className="space-y-3 mb-5">
              <div className="flex items-center gap-3 text-[#5a6d62]">
                <div className="w-8 h-8 border border-[#c5a572]/30 flex items-center justify-center rounded"><Calendar className="w-4 h-4 text-[#c5a572]" /></div>
                <div><p className="text-xs text-[#5a6d62]/70">Date</p><p className="text-sm text-[#1a2f23] font-medium">{parseLocalDate(event.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p></div>
              </div>
              <div className="flex items-center gap-3 text-[#5a6d62]">
                <div className="w-8 h-8 border border-[#c5a572]/30 flex items-center justify-center rounded"><Clock className="w-4 h-4 text-[#c5a572]" /></div>
                <div><p className="text-xs text-[#5a6d62]/70">Time</p><p className="text-sm text-[#1a2f23] font-medium">{event.time}</p></div>
              </div>
              <div className="flex items-center gap-3 text-[#5a6d62]">
                <div className="w-8 h-8 border border-[#c5a572]/30 flex items-center justify-center rounded"><MapPin className="w-4 h-4 text-[#c5a572]" /></div>
                <div><p className="text-xs text-[#5a6d62]/70">Location</p><p className="text-sm text-[#1a2f23] font-medium">{event.location}</p></div>
              </div>
            </div>
            <div className="border-t border-[#c5a572]/20 pt-4">
              <h3 className="text-xs uppercase tracking-wider text-[#c5a572] mb-2">About This Event</h3>
              <div className="text-sm text-[#5a6d62] leading-relaxed rich-text-content" dangerouslySetInnerHTML={{ __html: event.description }} />
            </div>
            <div className="mt-6">
              <button onClick={onClose} className="px-4 py-2 text-sm border border-[#1a2f23]/20 text-[#1a2f23] rounded hover:bg-gray-50 transition-colors">Close</button>
            </div>
          </div>
          <div className="lg:w-[45%] order-1 lg:order-2 relative bg-gray-100 flex items-center justify-center rounded-t-lg lg:rounded-t-none lg:rounded-r-lg overflow-hidden">
            <button onClick={onClose} className="hidden lg:flex absolute top-3 right-3 z-10 w-8 h-8 bg-white/90 hover:bg-white text-gray-700 rounded-full items-center justify-center shadow-lg"><X className="w-4 h-4" /></button>
            {event.poster_url ? (
              <img src={event.poster_url} alt={event.title} className="w-full h-48 lg:h-full object-contain" />
            ) : (
              <div className="w-full h-48 lg:h-full bg-gradient-to-br from-[#1a2f23] to-[#2d4a3a] flex items-center justify-center">
                <div className="text-center text-white p-6">
                  <Calendar className="w-12 h-12 text-[#c5a572] mx-auto mb-3" />
                  <p className="text-[#c5a572] uppercase tracking-widest text-xs">Community Event</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
